import { initReactI18next } from "react-i18next";

import i18next, { i18n } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import deTranslations from "../../assets/locales/de/translation.json";
import enTranslations from "../../assets/locales/en/translation.json";
import frTranslations from "../../assets/locales/fr/translation.json";
import { customerName } from "../../environmentConfiguration";
import { customerOverridesByLanguage } from "./CustomerOverridesByLanguage";
import { FallbackLocalTranslationsLoader } from "./FallbackLocalTranslationLoader";

type Dictionary = Record<string, unknown>;

const mergeDeep = (base: Dictionary, overrides: Dictionary): Dictionary => {
  const result: Dictionary = { ...base };

  const isObject = (value: unknown): value is Dictionary =>
    typeof value === "object" && value !== null && !Array.isArray(value);

  for (const [key, overrideValue] of Object.entries(overrides)) {
    const baseValue = result[key];
    result[key] = isObject(baseValue) && isObject(overrideValue) ? mergeDeep(baseValue, overrideValue) : overrideValue;
  }

  return result;
};

const customerOverrides = customerOverridesByLanguage[customerName] ?? {
  de: {},
  en: {},
  fr: {},
};

export const initLocalization = async (): Promise<i18n> => {
  const mergedDe = mergeDeep(deTranslations as Dictionary, customerOverrides.de);
  const mergedEn = mergeDeep(enTranslations as Dictionary, customerOverrides.en);
  const mergedFr = mergeDeep(frTranslations as Dictionary, customerOverrides.fr);

  await i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        de: { translation: mergedDe },
        en: { translation: mergedEn },
        fr: { translation: mergedFr },
      },
      fallbackLng: "de",
      supportedLngs: ["de", "en", "fr"],
      detection: {
        order: ["localStorage", "navigator"],
        caches: ["localStorage"],
        lookupLocalStorage: "i18nextLng",
      },
      debug: false,
      interpolation: {
        escapeValue: false,
      },
      maxRetries: 2,
      backend: {
        backends: [FallbackLocalTranslationsLoader],
        backendOptions: [
          {
            loadPath: `/src/assets/locales/{{lng}}/translation.json?url`, // Local fallback translationsin public folder
          },
        ],
      },
    });

  return i18next;
};
